/**
 * Floor icons — the symbols a washer or cashier reads INSTEAD of words.
 *
 * Most people working a Malaysian carwash floor come from Bangladesh, Nepal,
 * Indonesia or Myanmar and may read none of the app's four languages (owner,
 * 2026-10-16: "symbol is priority"). So the queue and the till lead with these
 * glyphs and put a short label underneath for whoever can read it. Each one is
 * drawn to survive being shown at 32–48px on a phone in a wet bay: one idea per
 * glyph, closed shapes, no hairline detail that disappears in sunlight.
 *
 * Same contract as `./index.tsx`: 16×16 viewBox, `currentColor`, 1.4 stroke,
 * rounded joins, `aria-hidden` — the call site supplies the accessible name.
 */

interface IconProps {
  readonly size?: number;
  readonly className?: string;
  readonly style?: React.CSSProperties;
}

function Svg({
  size = 16,
  className,
  style,
  children,
}: IconProps & { readonly children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
      style={style}
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

/** Waiting — the hourglass, with sand settled at the bottom. */
export function HourglassIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M4 2h8M4 14h8" />
      <path d="M5 2v2.2c0 1.3 1.2 2.4 3 3.8 1.8-1.4 3-2.5 3-3.8V2" />
      <path d="M5 14v-2.2c0-1.3 1.2-2.4 3-3.8 1.8 1.4 3 2.5 3 3.8V14" />
      <path d="M6.3 13.2h3.4L8 11.4z" fill="currentColor" />
    </Svg>
  );
}

/** Washing — a water drop. */
export function WaterIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M8 1.8c2.3 2.9 4 5.1 4 7.2a4 4 0 0 1-8 0c0-2.1 1.7-4.3 4-7.2z" />
      <path d="M6.2 9.4a1.9 1.9 0 0 0 1.6 1.8" />
    </Svg>
  );
}

/** Done / ready / confirmed. */
export function CheckIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M3 8.4l3.1 3.1L13 4.6" strokeWidth="1.9" />
    </Svg>
  );
}

/** Start — a solid play triangle. */
export function PlayIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path
        d="M5 3.3v9.4a.6.6 0 0 0 .9.5l7.2-4.7a.6.6 0 0 0 0-1L5.9 2.8a.6.6 0 0 0-.9.5z"
        fill="currentColor"
      />
    </Svg>
  );
}

/** Try again — a circular arrow. */
export function RetryIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M13 8a5 5 0 1 1-1.6-3.7" />
      <path d="M11.8 1.9v2.7H9.1" />
    </Svg>
  );
}

/** Failed / close. */
export function CloseIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M4 4l8 8M12 4l-8 8" strokeWidth="1.8" />
    </Svg>
  );
}

/** Elapsed time. */
export function ClockIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4.7V8l2.3 1.5" />
    </Svg>
  );
}

/** Sub-stage: drying — moving air. */
export function WindIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M2 6h7.5a2 2 0 1 0-2-2" />
      <path d="M2 9.5h9.5a2 2 0 1 1-2 2" />
      <path d="M2 12.5h3.5" />
    </Svg>
  );
}

/** Sub-stage: quality check — an inspecting eye. */
export function EyeIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M1.8 8s2.3-4.3 6.2-4.3S14.2 8 14.2 8s-2.3 4.3-6.2 4.3S1.8 8 1.8 8z" />
      <circle cx="8" cy="8" r="1.9" />
    </Svg>
  );
}

/** Payment: cash — a banknote. */
export function CashIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="1.5" y="4" width="13" height="8" rx="1.5" />
      <circle cx="8" cy="8" r="1.8" />
      <path d="M4 6.3v.01M12 9.7v.01" strokeWidth="1.8" />
    </Svg>
  );
}

/** Payment: DuitNow / any QR. */
export function QrIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="2" y="2" width="4.5" height="4.5" rx="1" />
      <rect x="9.5" y="2" width="4.5" height="4.5" rx="1" />
      <rect x="2" y="9.5" width="4.5" height="4.5" rx="1" />
      <path d="M9.5 9.5h1.8v1.8M14 9.5v.01M12.4 12.4H14V14M9.5 14h.01" />
    </Svg>
  );
}

/** Payment: card. */
export function CardIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" />
      <path d="M1.5 6.5h13M4 10h2.5" />
    </Svg>
  );
}

/** Payment: e-wallet on a phone (TNG, GrabPay). */
export function PhoneIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="4.5" y="1.5" width="7" height="13" rx="1.5" />
      <path d="M7.2 12.2h1.6" />
    </Svg>
  );
}

/** Payment: bank transfer. */
export function BankIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M2 6l6-3.5L14 6z" />
      <path d="M4 7.5v4.5M8 7.5v4.5M12 7.5v4.5M2 14h12" />
    </Svg>
  );
}

/** Vehicle: SUV — taller body, squarer back. */
export function SuvIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M2.5 10.5V5.8A1.3 1.3 0 0 1 3.8 4.5h6.6l2.4 2.9.7.5v2.6" />
      <path d="M2 10.5h12" />
      <circle cx="5" cy="11.4" r="1.2" />
      <circle cx="11" cy="11.4" r="1.2" />
    </Svg>
  );
}

/** Vehicle: MPV / van — long roof, sloped nose. */
export function VanIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M1.8 10.5V5A1.3 1.3 0 0 1 3.1 3.7h7.6l3.5 3.9v2.9" />
      <path d="M1.8 10.5h12.4M6.5 3.7v3.6h7" />
      <circle cx="4.8" cy="11.4" r="1.2" />
      <circle cx="11.2" cy="11.4" r="1.2" />
    </Svg>
  );
}

/** Vehicle: motorcycle. */
export function BikeIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="3.8" cy="11" r="2.3" />
      <circle cx="12.2" cy="11" r="2.3" />
      <path d="M3.8 11l3-4.3h3.4l2 4.3M6.8 6.7L5.9 4.6H4.4M10.2 6.7l.9-2.1h1.5" />
    </Svg>
  );
}
